import {
    getRetentionInsights,
    getInitialState,
    buildRequestBody,
    transformInsightsResponse,
    buildAppDataFromRetentionState,
} from './retention-trends.utils.js';
import { hasSubscriptionFlag } from '@/data/flags/local-flags';
import { StreamingProcessor } from '../ask-pendo/ask-pendo.streaming-processor.js';

const retentionTrends = {
    namespaced: true,
    state: getInitialState(),
    mutations: {
        setRetentionInsights (state, { insights }) {
            state.insights = insights;
        },
        setLoading (state, { isLoading }) {
            state.isLoading = isLoading;
        },
        setError (state, { error }) {
            state.error = error;
        },
    },
    actions: {
        async fetchRetentionInsights ({ commit, rootGetters, dispatch }, { options = {} } = {}) {
            commit('setLoading', { isLoading: true });
            commit('setError', { error: null });

            try {
                await dispatch('savedReports/loadAll', {}, { root: true });

                const appId = rootGetters['trendsSettings/getApp']?.id;
                const includeAnonymous = hasSubscriptionFlag('showAnonymous');
                const requestBody = buildRequestBody(appId, includeAnonymous);

                const data = await getRetentionInsights(requestBody, options);

                if (data instanceof ReadableStream) {
                    const streamingProcessor = new StreamingProcessor({
                        onData: (_chunk) => {},
                        onEnd: (finalState) => {
                            if (!finalState) {
                                commit('setError', { error: 'Stream ended without final state' });
                                commit('setLoading', { isLoading: false });

                                return;
                            }

                            const appData = buildAppDataFromRetentionState(finalState, rootGetters, appId);

                            const transformedInsights = transformInsightsResponse(finalState, appData);

                            commit('setRetentionInsights', { insights: transformedInsights });
                            commit('setLoading', { isLoading: false });
                        },
                        onError: (error) => {
                            commit('setError', { error: error.message || 'Failed to fetch retention insights' });
                            commit('setLoading', { isLoading: false });
                            throw error;
                        },
                    });

                    await streamingProcessor.process(data);

                    return;
                }

                const appData = buildAppDataFromRetentionState(data?.final_state, rootGetters);

                const transformedInsights = transformInsightsResponse(data.final_state, appData);

                commit('setRetentionInsights', { insights: transformedInsights });
                commit('setLoading', { isLoading: false });

                return transformedInsights;
            } catch (error) {
                commit('setError', { error: error.message || 'Failed to fetch retention insights' });
                commit('setLoading', { isLoading: false });
                throw error;
            }
        },
    },
    getters: {
        retentionInsights: (state) => state.insights || [],
        isLoading: (state) => state.isLoading,
        hasError: (state) => !!state.error,
        error: (state) => state.error,
    },
};

export default retentionTrends;

